import { useQuery } from '@apollo/client';
import type { GridColDef } from '@mui/x-data-grid-premium';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import type { Attribute } from '../attributes/attributes';
import { useFieldDefinitionData } from '../attributes/attributesApi';
import { SECURITY_ACTIONS, useSecurity } from '../common/auth/Security';
import { UserContext } from '../common/auth/UserContext';
import { FETCH_POLICY_CONFIG } from '../common/constants/constants';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import { FEATURE_CONSTANTS } from '../common/constants/featureConstants';
import { useMutationWithContext } from '../common/hooks/useMutationWithContext';
import { searchClient, slimSearchQuery } from '../common/list/slimQuery';
import SRDataImport from '../common/SRDataImport/SRDataImport';
import SRDataImportDetails from '../common/SRDataImport/SRDataImportDetails';
import useHistory from '../common/utils/useHistory';
import { valueGetter } from '../supply/opportunity/details/ContactListUtils';
import type { DoSaveParamsWithoutManaged } from './bulkSaveProductionUtils';
import { doSave } from './helpers/bulkSaveProductions';
import { bulkSaveQuery, client } from './helpers/production';

import { importPlaceholder, productionImportColDefs } from './helpers/productionImportUtils';
import { convertFieldDefinitionsToColDef } from './helpers/productionUtils';
import { ALT_ID_PREFIX, PRODUCTION_MODE, STATE_TYPE } from './productionConstants';
import { ProductionModes } from './ProductionList';

export default function ProductionImport({ onSave, importPanelOpen, onClose }) {
    const { searchParams } = useHistory();
    const { userProfile, application, activeOrganizationAccount } = useContext(UserContext);
    const [bulkSave] = useMutationWithContext(bulkSaveQuery, client);
    const [importDetails, setImportDetails] = useState(undefined);
    const [isImportDetailsDialogOpen, setIsImportDetailsDialogOpen] = useState(true);
    const [importColDefs, setImportColDefs] = useState(productionImportColDefs);
    const [fieldDefinitionReferences, setFieldDefinitionReferences] = useState<Attribute[]>([]);
    const defaultOwnerOrganizationId =
        userProfile?.currentOwnerOrganizationIds?.length > 0 ? userProfile.currentOwnerOrganizationIds[0] : '';
    const { data } = useQuery(slimSearchQuery('AlternateIdLabel'), {
        client: searchClient,
        variables: {
            entity: 'AlternateIdLabel',
            page: { from: 0, size: 50 },
            tokenize: true,
            sortBy: undefined
        },
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
    });
    const COLUMN_VISIBILITY = { ID: false };

    const { fieldDefinitionData } = useFieldDefinitionData(
        [ENTITY_CONSTANTS.production],
        undefined,
        false,
        'sequence_ASC'
    );

    const { allowed: canManageProductionWorkspace } = useSecurity(SECURITY_ACTIONS.EDIT, {
        featureName: FEATURE_CONSTANTS.productionWorkspace
    });
    const isManagedProduction =
        searchParams.get(PRODUCTION_MODE) === ProductionModes.workspace && canManageProductionWorkspace;

    const onConfirm = () => {
        setIsImportDetailsDialogOpen(false);
    };

    const doSaveForImport = useCallback(
        (...args: DoSaveParamsWithoutManaged) => {
            const [
                setIsSaving,
                rows,
                isAltColDef,
                altColDefs,
                colDefs,
                defaultColumns,
                setErrors,
                onClose,
                onSave,
                bulkSave,
                activeOrganizationAccount,
                setShowImportDetails,
                setShowImportDetailsDialog,
                state,
                attachmentData,
                attachmentReferenceEditor
            ] = args;
            return doSave(
                setIsSaving,
                rows,
                isAltColDef,
                altColDefs,
                colDefs,
                defaultColumns,
                setErrors,
                onClose,
                onSave,
                bulkSave,
                activeOrganizationAccount,
                setShowImportDetails,
                setShowImportDetailsDialog,
                state,
                attachmentData,
                attachmentReferenceEditor,
                isManagedProduction,
                defaultOwnerOrganizationId,
                fieldDefinitionReferences
            );
        },
        [isManagedProduction, defaultOwnerOrganizationId, fieldDefinitionReferences]
    );

    useEffect(() => {
        if (data) {
            const altIdColDefs = data?.search.hits.items?.map((item) => {
                const col: GridColDef = {
                    field: `${ALT_ID_PREFIX}${item.name}`,
                    headerName: item.name,
                    flex: 1,
                    sortable: true,
                    type: 'string',
                    valueGetter
                };
                return col;
            });
            setImportColDefs([...productionImportColDefs, ...altIdColDefs]);
        }
    }, [data]);

    useEffect(() => {
        if (fieldDefinitionData) {
            const { colDef, colDefMeta } = convertFieldDefinitionsToColDef(
                fieldDefinitionData?.results?.hits?.items || []
            );
            setImportColDefs((prev) => [...prev, ...colDef]);
            setFieldDefinitionReferences(colDefMeta);
        }
    }, [fieldDefinitionData]);

    return (
        <>
            {importPanelOpen && (
                <SRDataImport
                    feature={'Production'}
                    columnVisibility={COLUMN_VISIBILITY}
                    primaryColDefs={importColDefs}
                    altColDefs={importColDefs}
                    onSave={onSave}
                    isUpload={importPanelOpen}
                    onClose={onClose}
                    doSave={doSaveForImport}
                    bulkSave={bulkSave}
                    title={'Import'}
                    saveLabel={'Save'}
                    editLabel={'Previous'}
                    setImportDetails={setImportDetails}
                    setShowImportDetailsDialog={setIsImportDetailsDialogOpen}
                    placeholder={importPlaceholder}
                    disableTextBasedSelector={false}
                    category={'Productions'}
                    state={STATE_TYPE.managed}
                />
            )}

            {importDetails && (
                <SRDataImportDetails open={isImportDetailsDialogOpen} onConfirm={onConfirm} details={importDetails} />
            )}
        </>
    );
}
